import * as Yup from 'yup';
import i18n from '../../i18n';
import { getDaysPerMonth, getYearsTillNow } from './datesHelpers';
import { formatMoney } from './numeralHelpers';

export const getValidationSchema = (
  {
    fields = [],
    // countries = [],
    minAmount = 1,
    maxAmount = 500000000,
    type = '',
    noValidation,
  } = {
    fields: [],
    minAmount: 1,
    maxAmount: 500000000,
    countries: [],
    countryCode: ',',
    type,
    noValidation,
    STATE_OPTIONS: [],
  },
) => {
  const allValidationBucket = {
    FirstName: Yup.string()
      .trim()
      .matches(
        /^(?!.*\p{Sc})(?!.*\p{Cc})[^\p{Cc}!@#$%*()+=?:;"`[\]{}<>~^_/\\|]*$/u,
        i18n.t('fields.errors.validation.invalidCharacters'),
      )
      .max(50, i18n.t('fields.errors.validation.firstNameMaxLength'))
      .nullable()
      .required(i18n.t('fields.errors.validation.firstName')),
    MiddleName: Yup.string()
      .trim()
      .nullable()
      .max(50, i18n.t('fields.errors.validation.middleNameMaxLength'))
      .matches(
        /^(?!.*\p{Sc})(?!.*\p{Cc})[^\p{Cc}!@#$%*()+=?:;"`[\]{}<>~^_/\\|]*$/u,
        i18n.t(`fields.errors.validation.invalidCharacters`),
      ),
    LastName: Yup.string()
      .trim()
      .matches(
        /^(?!.*\p{Sc})(?!.*\p{Cc})[^\p{Cc}!@#$%*()+=?:;"`[\]{}<>~^_/\\|]*$/u,
        i18n.t(`fields.errors.validation.invalidCharacters`),
      )
      .max(50, i18n.t('fields.errors.validation.lastNameMaxLength'))
      .nullable()
      .required(i18n.t('fields.errors.validation.lastName')),
    Suffix: Yup.object().nullable(),
    Month: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.month`))
      .min(1, i18n.t(`fields.errors.validation.minMonth`))
      .max(12, i18n.t(`fields.errors.validation.maxMonth`))
      .required(),
    Year: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.year`))
      .min(
        parseInt(
          getYearsTillNow().reverse()[getYearsTillNow().reverse().length - 1],
          10,
        ),
        i18n.t(`fields.errors.validation.minYear`),
      )
      .max(
        parseInt(getYearsTillNow().reverse()[0], 10),
        i18n.t(`fields.errors.validation.maxYear`),
      )
      .required(),
    Day: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.day`))
      .min(1, i18n.t(`fields.errors.validation.minDay`))
      .when(['Month', 'Year'], (Month, Year, schema) =>
        Month && Month > 0 && Month < 13 && Year
          ? schema.max(
              getDaysPerMonth(
                Month.toString().padStart(2, '0'),
                Year.toString().padStart(2, '0'),
              ).length,
              i18n.t(`fields.errors.validation.maxDay`),
            )
          : schema.max(30, i18n.t(`fields.errors.validation.maxDay`)),
      )
      .required(),
    IdentityCountry: Yup.object()
      .typeError(i18n.t('fields.errors.validation.countryRequired'))
      .required(i18n.t('fields.errors.validation.countryRequired')),
    Country: Yup.object()
      .typeError(i18n.t('fields.errors.validation.countryRequired'))
      .required(i18n.t('fields.errors.validation.countryRequired')),
    Email: Yup.string()
      .trim()
      .email(i18n.t('fields.errors.validation.emailFormat'))
      .max(50, i18n.t('fields.errors.validation.emailMaxLength'))
      .matches(
        /^[a-zA-Z0-9@._-]*$/,
        i18n.t(`fields.errors.validation.invalidCharacters`),
      )
      .required(i18n.t('fields.errors.validation.emailRequired')),
    Username: Yup.object()
      .typeError(i18n.t('fields.errors.validation.iraType'))
      .shape({
        value: Yup.string()
          .typeError(i18n.t('fields.errors.validation.iraType'))
          .required(i18n.t('fields.errors.validation.iraType')),
        label: Yup.string()
          .typeError(i18n.t('fields.errors.validation.iraType'))
          .required(i18n.t('fields.errors.validation.iraType')),
      }),
    Password: Yup.string()
      .trim()
      .matches(
        /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/,
        i18n.t(`fields.errors.validation.passwordStrength`),
      )
      .matches(
        /^[a-zA-Z0-9!@#$%^&*()_+[\]{};:'",.<>?/\\|]*$/,
        i18n.t(`fields.errors.validation.invalidCharacters`),
      )
      .max(50, i18n.t('fields.errors.validation.passwordMaxLength'))
      .required(i18n.t(`fields.errors.validation.password`)),
    SSN: Yup.string()
      .typeError(i18n.t('fields.errors.validation.ssnRequired'))
      .required()
      .test(i18n.t('fields.errors.validation.ssnDigits'), (value) =>
        /^(?!(000|666|999))\d{3}-?(?!(00|66|99))\d{2}-?(?!(0000|6666|9999))\d{4}$/.test(
          value,
        ),
      )
      .min(11, i18n.t('fields.errors.validation.einValidation'))
      .max(11, i18n.t('fields.errors.validation.einValidation')),
    NoEin: Yup.bool(),
    Ein: Yup.string().when('NoEin', {
      is: false,
      then: Yup.string()
        .required(i18n.t('fields.errors.validation.einRequired'))
        .test(i18n.t('fields.errors.validation.einDigits'), (value) =>
          /^(?!(00|66|99))\d{2}-?(?!(0000000|6666666|9999999))\d{7}$/.test(
            value,
          ),
        ),
      finally: Yup.string().nullable(),
    }),
    IDType: Yup.number()
      .nullable()
      .typeError(i18n.t('fields.errors.validation.idTypeRequired'))
      .required(i18n.t('fields.errors.validation.idTypeRequired')),
    IDNumber: Yup.string()
      .nullable()
      .max(50, i18n.t('fields.errors.validation.idNumberMaxLength'))
      .required(i18n.t('fields.errors.validation.idNumberRequired')),
    BusinessName: Yup.string()
      .matches(
        /^(?!.*\p{Sc})(?!.*\p{Cc})[^\p{Cc}!@#$%*()+=?:;"`[\]{}<>~^_/\\|]*$/u,
        i18n.t(`fields.errors.validation.invalidCharacters`),
      )
      .max(100, i18n.t('fields.errors.validation.businessNameMax'))
      .required(i18n.t('fields.errors.validation.businessName')),
    TrustName: Yup.string()
      .matches(
        /^(?!.*\p{Sc})(?!.*\p{Cc})[^\p{Cc}!@#$%*()+=?:;"`[\]{}<>~^_/\\|]*$/u,
        i18n.t(`fields.errors.validation.invalidCharacters`),
      )
      .max(100, i18n.t('fields.errors.validation.trustNameMax'))
      .required(i18n.t('fields.errors.validation.trustName')),
    Amount: noValidation
      ? Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
          .typeError(i18n.t('fields.errors.validation.mustBeNumber'))
          .positive(i18n.t('fields.errors.validation.greaterThanZero'))
          .required(i18n.t('fields.errors.validation.amount'))
      : Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
          .typeError(i18n.t('fields.errors.validation.mustBeNumber'))
          .positive(i18n.t('fields.errors.validation.greaterThanZero'))
          .min(
            minAmount,
            i18n.t('fields.errors.validation.minAmount', {
              minAmount: formatMoney(minAmount, 0, 0),
              type,
            }),
          )
          .max(
            maxAmount,
            i18n.t('fields.errors.validation.maxAmount', {
              maxAmount: formatMoney(maxAmount, 0, 0),
              type,
            }),
          )
          .required(i18n.t('fields.errors.validation.amount')),
    GoldPercentage: Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
      .typeError(i18n.t('fields.errors.validation.mustBeNumber'))
      .required(),
    SilverPercentage: Yup.number(
      i18n.t('fields.errors.validation.mustBeNumber'),
    )
      .typeError(i18n.t('fields.errors.validation.mustBeNumber'))
      .required(),
    percentage: Yup.number(),
    Street: Yup.string()
      .trim()
      .typeError(i18n.t('fields.errors.validation.streetAddress'))
      .max(50, i18n.t('fields.errors.validation.streetAddressMaxLength'))
      .required(i18n.t('fields.errors.validation.streetAddress')),
    AptOrSuite: Yup.string().trim().nullable(),
    City: Yup.string()
      .trim()
      .typeError(i18n.t('fields.errors.validation.city'))
      .max(50, i18n.t('fields.errors.validation.cityMaxLength'))
      .required(i18n.t('fields.errors.validation.city')),
    PostalCode: Yup.string()
      .trim()
      .typeError(i18n.t('fields.errors.validation.postalCode'))
      .required(i18n.t('fields.errors.validation.postalCode')),
    State: Yup.mixed().test(
      'valid-state',
      i18n.t('fields.errors.validation.stateRequired'),
      (value) => {
        if (typeof value === 'string') {
          return value.trim() !== '';
        }
        return (
          value &&
          typeof value === 'object' &&
          typeof value.value === 'string' &&
          value.value.trim() !== ''
        );
      },
    ),
    AutoDeposit: Yup.boolean().oneOf([true]).required(),
    FundAmount: noValidation
      ? Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
          .positive(i18n.t('fields.errors.validation.greaterThanZero'))
          .typeError(i18n.t('fields.errors.validation.mustBeNumber'))
          .required(i18n.t('fields.errors.validation.depositAmount'))
      : Yup.number(i18n.t('fields.errors.validation.mustBeNumber'))
          .min(
            minAmount,
            i18n.t('fields.errors.validation.minAmount', {
              minAmount: formatMoney(minAmount, 0, 0),
              type,
            }),
          )
          .max(
            maxAmount,
            i18n.t('fields.errors.validation.maxAmount', {
              maxAmount: formatMoney(maxAmount, 0, 0),
              type,
            }),
          )
          .positive(i18n.t('fields.errors.validation.greaterThanZero'))
          .typeError(i18n.t('fields.errors.validation.mustBeNumber'))
          .required(i18n.t('fields.errors.validation.depositAmount')),
    CustodianName: Yup.string().required(
      i18n.t('fields.errors.validation.custodianName'),
    ),
    AccountNumber: Yup.string()
      .typeError(i18n.t('fields.errors.validation.accountNumber'))
      .required(i18n.t('fields.errors.validation.accountNumber')),

    CustodianPhone: Yup.string().required(
      i18n.t('fields.errors.validation.custodianPhone'),
    ),
    PersonPhoneNumber: Yup.string()
      .required(i18n.t('fields.errors.validation.personPhone'))
      .max(50, i18n.t('fields.errors.validation.personPhoneMaxLength')),
    CustodianFax: Yup.string().required(
      i18n.t('fields.errors.validation.custodianFax'),
    ),
    AccountType: Yup.object()
      .typeError(i18n.t('fields.errors.validation.iraType'))
      .shape({
        value: Yup.string()
          .typeError(i18n.t('fields.errors.validation.iraType'))
          .required(i18n.t('fields.errors.validation.iraType')),
        label: Yup.string()
          .typeError(i18n.t('fields.errors.validation.iraType'))
          .required(i18n.t('fields.errors.validation.iraType')),
      }),
    Custodian: Yup.string()
      .typeError(i18n.t('fields.errors.validation.custodian'))
      .required(i18n.t('fields.errors.validation.custodian')),
    EmploymentStatus: Yup.object()
      // .shape({
      //   value: Yup.number()
      //     .typeError(i18n.t('fields.errors.validation.employmentStatus'))
      //     .required(i18n.t('fields.errors.validation.employmentStatus')),
      //   label: Yup.string()
      //     .typeError(i18n.t('fields.errors.validation.employmentStatus'))
      //     .required(i18n.t('fields.errors.validation.employmentStatus')),
      // })
      .typeError(i18n.t('fields.errors.validation.employmentStatus'))
      .required(i18n.t('fields.errors.validation.employmentStatus')),
    OccupationCategory: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.object()
        // .shape({
        //   value: Yup.number()
        //     .typeError(i18n.t('fields.errors.validation.occupationCategory'))
        //     .required(i18n.t('fields.errors.validation.occupationCategory')),
        //   label: Yup.string()
        //     .typeError(i18n.t('fields.errors.validation.occupationCategory'))
        //     .required(i18n.t('fields.errors.validation.occupationCategory')),
        // })
        .typeError(i18n.t('fields.errors.validation.occupationCategory'))
        .required(i18n.t('fields.errors.validation.occupationCategory')),
      otherwise: Yup.object()
        // .shape({
        //   value: Yup.number()
        //     .typeError(i18n.t('fields.errors.validation.occupationCategory'))
        //     .required(i18n.t('fields.errors.validation.occupationCategory')),
        //   label: Yup.string()
        //     .typeError(i18n.t('fields.errors.validation.occupationCategory'))
        //     .required(i18n.t('fields.errors.validation.occupationCategory')),
        // })
        .typeError(i18n.t('fields.errors.validation.priorOccupationCategory'))
        .required(i18n.t('fields.errors.validation.priorOccupationCategory')),
    }),
    Occupation: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.object()
        // .shape({
        //   value: Yup.number()
        //     .typeError(i18n.t('fields.errors.validation.occupation'))
        //     .required(i18n.t('fields.errors.validation.occupation')),
        //   label: Yup.string()
        //     .typeError(i18n.t('fields.errors.validation.occupation'))
        //     .required(i18n.t('fields.errors.validation.occupation')),
        // })
        .typeError(i18n.t('fields.errors.validation.occupation'))
        .required(i18n.t('fields.errors.validation.occupation')),
      otherwise: Yup.object()
        // .shape({
        //   value: Yup.number()
        //     .typeError(i18n.t('fields.errors.validation.occupation'))
        //     .required(i18n.t('fields.errors.validation.occupation')),
        //   label: Yup.string()
        //     .typeError(i18n.t('fields.errors.validation.occupation'))
        //     .required(i18n.t('fields.errors.validation.occupation')),
        // })
        .typeError(i18n.t('fields.errors.validation.priorOccupation'))
        .required(i18n.t('fields.errors.validation.priorOccupation')),
    }),
    SourceOfIncome: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value !== 1,
      then: Yup.object()
        // .shape({
        //   value: Yup.number()
        //     .typeError(i18n.t('fields.errors.validation.sourceOfIncome'))
        //     .required(i18n.t('fields.errors.validation.sourceOfIncome')),
        //   label: Yup.string()
        //     .typeError(i18n.t('fields.errors.validation.sourceOfIncome'))
        //     .required(i18n.t('fields.errors.validation.sourceOfIncome')),
        // })
        .typeError(i18n.t('fields.errors.validation.sourceOfIncome'))
        .required(i18n.t('fields.errors.validation.sourceOfIncome')),
      otherwise: Yup.mixed().nullable(),
    }),
    EmployerName: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.string()
        .typeError(i18n.t('fields.errors.validation.employerName'))
        .max(50, i18n.t('fields.errors.validation.employerNameMaxLength'))
        .required(i18n.t('fields.errors.validation.employerName')),
      otherwise: Yup.mixed().nullable(),
    }),
    EmployerAddressLine: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.string()
        .typeError(i18n.t('fields.errors.validation.employerStreet'))
        .max(50, i18n.t('fields.errors.validation.employerStreetMaxLength'))
        .required(i18n.t('fields.errors.validation.employerStreet')),
      otherwise: Yup.mixed().nullable(),
    }),
    EmployerCity: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.string()
        .typeError(i18n.t('fields.errors.validation.employerCity'))
        .max(50, i18n.t('fields.errors.validation.employerCityMaxLength'))
        .required(i18n.t('fields.errors.validation.employerCity')),
      otherwise: Yup.mixed().nullable(),
    }),
    EmployerZipCode: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.string()
        .typeError(i18n.t('fields.errors.validation.employerPostalCode'))
        .required(i18n.t('fields.errors.validation.employerPostalCode')),
      otherwise: Yup.mixed().nullable(),
    }),
    EmployerState: Yup.mixed().when('EmploymentStatus', {
      is: (status) => status?.value === 1,
      then: Yup.object()
        .typeError(i18n.t(`fields.errors.validation.employerStateRequired`))
        .shape({
          value: Yup.string()
            .typeError(i18n.t(`fields.errors.validation.employerStateRequired`))
            .required(i18n.t('fields.errors.validation.employerStateRequired')),
        }),
      otherwise: Yup.mixed().nullable(),
    }),
    AccountPurpose: Yup.number()
      .nullable()
      .required(i18n.t('fields.errors.validation.personalGoal')),
    InitialFundingSourceTypes: Yup.array()
      .min(1, i18n.t('fields.errors.validation.personalGoal'))
      .required(i18n.t('fields.errors.validation.personalGoal')),
    OngoingFundingSourceTypes: Yup.array()
      .min(1, i18n.t('fields.errors.validation.personalGoal'))
      .required(i18n.t('fields.errors.validation.personalGoal')),
  };

  const validationSchema = fields.reduce(
    (acc, field) => ({
      ...acc,
      [field]: allValidationBucket[field],
    }),
    {},
  );

  return validationSchema;
};
